import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { getAcademicYearsDirect } from '@timetable/api';
import type { AcademicYear } from '@timetable/types';

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string;

interface CheckResult {
  label: string;
  description: string;
  passed: boolean | null; // null = pending
}

interface PrePublishValidationDialogProps {
  open: boolean;
  year: AcademicYear;
  schoolId: string;
  onConfirm: () => void;
  onClose: () => void;
  isPublishing: boolean;
  publishError: string | null;
}

async function runValidation(
  yearId: string,
  accessToken: string
): Promise<CheckResult[]> {
  const headers = {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${accessToken}`,
  };

  // Check (a): at least one timetable record
  const recordsRes = await fetch(
    `${SUPABASE_URL}/rest/v1/timetable_records?academic_year_id=eq.${yearId}&select=id&limit=1`,
    { headers }
  );
  const records = recordsRes.ok ? await recordsRes.json() : [];
  const hasRecords = records.length > 0;

  // Check (b): all classes with records have class_schedule_id
  const classesRes = await fetch(
    `${SUPABASE_URL}/rest/v1/timetable_records?academic_year_id=eq.${yearId}&select=class_section:class_sections(class:classes(id,class_name,class_schedule_id))`,
    { headers }
  );
  let allClassesHaveSchedule = true;
  let missingScheduleNames: string[] = [];
  if (classesRes.ok) {
    const classRecords = await classesRes.json();
    const missing = classRecords.filter(
      (r: any) => r.class_section?.class && !r.class_section.class.class_schedule_id
    );
    if (missing.length > 0) {
      allClassesHaveSchedule = false;
      missingScheduleNames = [
        ...new Set<string>(missing.map((r: any) => r.class_section.class.class_name as string)),
      ];
    }
  }

  // Check (c): no conflicts — fetch records with period time data
  const conflictRes = await fetch(
    `${SUPABASE_URL}/rest/v1/timetable_records?academic_year_id=eq.${yearId}&select=id,teacher_id,room_id,class_section_id,day_of_week,period:schedule_periods(id,time_start,time_end)`,
    { headers }
  );
  let conflictCount = 0;
  if (conflictRes.ok) {
    const conflictRecords: any[] = await conflictRes.json();
    conflictCount = detectConflicts(conflictRecords);
  }

  return [
    {
      label: 'Timetable records exist',
      description: hasRecords
        ? 'At least one timetable record is assigned to this year.'
        : 'No timetable records found. Add at least one record before publishing.',
      passed: hasRecords,
    },
    {
      label: 'All classes have a schedule assigned',
      description: allClassesHaveSchedule
        ? 'All classes with timetable records have a class schedule assigned.'
        : `The following classes are missing a schedule: ${missingScheduleNames.join(', ')}. Assign a class schedule before publishing.`,
      passed: allClassesHaveSchedule,
    },
    {
      label: 'No scheduling conflicts',
      description:
        conflictCount === 0
          ? 'No teacher, room, or class conflicts detected.'
          : `${conflictCount} conflict(s) detected. Resolve all conflicts before publishing.`,
      passed: conflictCount === 0,
    },
  ];
}

function detectConflicts(records: any[]): number {
  let count = 0;

  function overlaps(aS: string, aE: string, bS: string, bE: string) {
    return aS < bE && bS < aE;
  }

  function checkGroup(groups: Map<string, any[]>) {
    for (const group of groups.values()) {
      const byDay = new Map<string, any[]>();
      for (const r of group) {
        if (!byDay.has(r.day_of_week)) byDay.set(r.day_of_week, []);
        byDay.get(r.day_of_week)!.push(r);
      }
      for (const dayRecs of byDay.values()) {
        for (let i = 0; i < dayRecs.length; i++) {
          for (let j = i + 1; j < dayRecs.length; j++) {
            const a = dayRecs[i];
            const b = dayRecs[j];
            if (
              a.period?.time_start &&
              a.period?.time_end &&
              b.period?.time_start &&
              b.period?.time_end &&
              overlaps(a.period.time_start, a.period.time_end, b.period.time_start, b.period.time_end)
            ) {
              count++;
            }
          }
        }
      }
    }
  }

  const byTeacher = new Map<string, any[]>();
  const byRoom = new Map<string, any[]>();
  const bySection = new Map<string, any[]>();

  for (const r of records) {
    if (r.teacher_id) {
      if (!byTeacher.has(r.teacher_id)) byTeacher.set(r.teacher_id, []);
      byTeacher.get(r.teacher_id)!.push(r);
    }
    if (r.room_id) {
      if (!byRoom.has(r.room_id)) byRoom.set(r.room_id, []);
      byRoom.get(r.room_id)!.push(r);
    }
    if (r.class_section_id) {
      if (!bySection.has(r.class_section_id)) bySection.set(r.class_section_id, []);
      bySection.get(r.class_section_id)!.push(r);
    }
  }

  checkGroup(byTeacher);
  checkGroup(byRoom);
  checkGroup(bySection);

  return count;
}

export const PrePublishValidationDialog: React.FC<PrePublishValidationDialogProps> = ({
  open,
  year,
  schoolId: _schoolId,
  onConfirm,
  onClose,
  isPublishing,
  publishError,
}) => {
  const [checks, setChecks] = useState<CheckResult[] | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setChecks(null);
      setValidationError(null);
      return;
    }

    setValidating(true);
    setValidationError(null);

    const token = (() => {
      try {
        const raw = window.localStorage.getItem('supabase.auth.token');
        if (!raw) return null;
        return JSON.parse(raw)?.currentSession?.access_token ?? null;
      } catch {
        return null;
      }
    })();

    if (!token) {
      setValidating(false);
      setValidationError('Not authenticated');
      return;
    }

    runValidation(year.id, token)
      .then((results) => setChecks(results))
      .catch((err) => setValidationError(err.message ?? 'Validation failed'))
      .finally(() => setValidating(false));
  }, [open, year.id]);

  const allPassed = checks?.every((c) => c.passed === true) ?? false;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pre-Publish Validation — {year.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            The following checks must pass before this year can be published.
          </Typography>

          {validating && (
            <Stack direction="row" spacing={2} alignItems="center" py={2}>
              <CircularProgress size={20} />
              <Typography variant="body2">Running validation checks…</Typography>
            </Stack>
          )}

          {validationError && (
            <Alert severity="error">{validationError}</Alert>
          )}

          {checks && (
            <List disablePadding>
              {checks.map((check, i) => (
                <ListItem key={i} disableGutters alignItems="flex-start">
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    {check.passed === null ? (
                      <PendingIcon color="disabled" />
                    ) : check.passed ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={check.label}
                    secondary={check.description}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 600,
                      color: check.passed === false ? 'error.main' : 'text.primary',
                    }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {publishError && (
            <Alert severity="error">
              <strong>Publish failed:</strong> {publishError}
            </Alert>
          )}

          {checks && allPassed && (
            <Alert severity="success">
              All checks passed. You can now publish this academic year.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPublishing}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="success"
          disabled={!allPassed || isPublishing || validating}
        >
          {isPublishing ? 'Publishing…' : 'Confirm Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
